import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import moment from "moment";
import dashboardServices from "app/services/dashboard-services";

// El panel de la tarjeta "Entregadas".
//
// La tarjeta dice "40 / 150" y ahi se acaba: no dice si esos 110 que faltan estan
// repartidos entre todos o los debe un solo camion que salio tarde. Este panel abre
// ese numero viaje por viaje, y por eso ordena de menor a mayor avance: arriba queda
// al que hay que mirarle la cara.
//
// Las cuentas son las mismas que las del tile a proposito. Si el panel contara
// distinto, el primer reflejo de quien lo abre seria desconfiar de los dos numeros.
const TONO = "#4E7A5E";

// El estado del viaje explica un avance bajo: 0 de 30 en un viaje que todavia no
// salio no es un problema, en uno en curso si.
const ESTADO = {
    planned: {label: "Planificado", color: "default"},
    pending: {label: "Pendiente", color: "default"},
    in_progress: {label: "En curso", color: "warning"},
    finished: {label: "Finalizado", color: "success"},
    cancelled: {label: "Cancelado", color: "error"}
};

const DeliveryProgress = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dia, setDia] = useState(moment().format("YYYY-MM-DD"));
    const [viajesAbiertos, setViajesAbiertos] = useState({});

    const cargar = useCallback(() => {
        setLoading(true);
        setError(null);

        dashboardServices
            .getDeliveryProgress({fecha: dia})
            .then((res) => setData(res))
            .catch(() => setError("No se pudo cargar el avance del día."))
            .finally(() => setLoading(false));
    }, [dia]);

    const alternarPedidos = (tripId) => {
        setViajesAbiertos((prev) => ({...prev, [tripId]: !prev[tripId]}));
    };

    useEffect(() => {
        cargar();
    }, [cargar]);

    const totales = data?.totals || {};
    const viajes = data?.trips || [];

    return (
        <Card sx={{mb: 3}}>
            <CardContent>
                <Stack
                    direction={{xs: "column", md: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "flex-start", md: "center"}}
                    spacing={1.5}
                    sx={{mb: 2}}
                >
                    <Stack spacing={0.5}>
                        <Typography variant="h4" sx={{mb: 0}}>Entregadas</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Entregas cerradas sobre lo planificado para el día, viaje por viaje.
                        </Typography>
                    </Stack>

                    <TextField
                        id="delivery-progress-dia"
                        label="Día"
                        type="date"
                        size="small"
                        value={dia}
                        onChange={(e) => setDia(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                </Stack>

                {loading && (
                    <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                        <CircularProgress/>
                    </Box>
                )}

                {!loading && error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && viajes.length === 0 && (
                    <Alert severity="info">No hay entregas planificadas para este día.</Alert>
                )}

                {!loading && !error && viajes.length > 0 && (
                    <>
                        <Stack direction="row" alignItems="baseline" spacing={1} sx={{mb: 1}}>
                            <Typography variant="h1" sx={{color: TONO, mb: 0}}>
                                {totales.delivered} / {totales.planned}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                entregas cerradas · faltan {totales.pending}
                            </Typography>
                        </Stack>

                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, totales.percent || 0)}
                            sx={{
                                height: 10,
                                borderRadius: 1,
                                mb: 3,
                                backgroundColor: "action.hover",
                                "& .MuiLinearProgress-bar": {backgroundColor: TONO}
                            }}
                        />

                        <Typography variant="h6" sx={{mb: 1}}>Por viaje</Typography>
                        <TableContainer sx={{maxHeight: 420}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Viaje</TableCell>
                                        <TableCell>Conductor</TableCell>
                                        <TableCell>Vehículo</TableCell>
                                        <TableCell>Estado</TableCell>
                                        <TableCell align="right">Pedidos</TableCell>
                                        <TableCell align="right">Avance</TableCell>
                                        <TableCell sx={{width: 140}}/>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {viajes.map((viaje) => {
                                        const estado = ESTADO[viaje.trip_state] || {label: viaje.trip_state, color: "default"};

                                        const pedidos = viaje.orders || [];
                                        const abierto = Boolean(viajesAbiertos[viaje.trip_id]);

                                        return (
                                            <React.Fragment key={viaje.trip_id}>
                                                <TableRow>
                                                    <TableCell>{viaje.trip_code || "—"}</TableCell>
                                                    <TableCell>{viaje.driver_name || "Sin asignar"}</TableCell>
                                                    <TableCell>{viaje.vehicle_code || "—"}</TableCell>
                                                    <TableCell>
                                                        <Chip size="small" label={estado.label} color={estado.color} variant="outlined"/>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.5}>
                                                            <Typography variant="body2">{pedidos.length}</Typography>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={abierto ? "Ocultar pedidos" : "Ver pedidos"}
                                                                onClick={() => alternarPedidos(viaje.trip_id)}
                                                            >
                                                                {abierto
                                                                    ? <KeyboardArrowUpRoundedIcon fontSize="small"/>
                                                                    : <KeyboardArrowDownRoundedIcon fontSize="small"/>}
                                                            </IconButton>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{whiteSpace: "nowrap"}}>
                                                        {viaje.delivered} / {viaje.planned}
                                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ml: 1}}>
                                                            {viaje.percent}%
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.min(100, viaje.percent || 0)}
                                                            sx={{
                                                                height: 8,
                                                                borderRadius: 1,
                                                                backgroundColor: "action.hover",
                                                                "& .MuiLinearProgress-bar": {backgroundColor: TONO}
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{p: 0, borderBottom: abierto ? undefined : 0}}>
                                                        <Collapse in={abierto} timeout="auto" unmountOnExit>
                                                            <Box sx={{p: 2, bgcolor: "action.hover"}}>
                                                                <Typography variant="subtitle2" sx={{mb: 1}}>Pedidos del viaje</Typography>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell>Pedido</TableCell>
                                                                            <TableCell>Cliente</TableCell>
                                                                            <TableCell>Estado</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {pedidos.map((pedido) => {
                                                                            const estadoPedido = pedido.state === "finished"
                                                                                ? "Entregado"
                                                                                : pedido.state === "cancelled"
                                                                                    ? "Cancelado"
                                                                                    : "Pendiente";

                                                                            return (
                                                                                <TableRow key={pedido.delivery_id}>
                                                                                    <TableCell>{pedido.order_code || "—"}</TableCell>
                                                                                    <TableCell>{pedido.customer_name || "—"}</TableCell>
                                                                                    <TableCell>{estadoPedido}</TableCell>
                                                                                </TableRow>
                                                                            );
                                                                        })}
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default DeliveryProgress;
